import { useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { Text } from '@savvy-kit/foundations/text';
import { DateTime } from 'luxon';
import ClientRoadmap from 'src/components/client-roadmap';
import { DashboardLayout } from 'src/components/dashboard/dashboard-layout';
import PortfolioSplashScreen from 'src/components/loading-and-splash/portfolio-splash-screen';
import { useSplashHold } from 'src/components/loading-and-splash/splash-hold';
import { ClientPortalHome } from 'src/features/client-portal/components/home/home';
import { ClientPortalVersionPending } from 'src/features/client-portal/components/version-pending';
import { useClientPortalRedesign } from 'src/features/client-portal/hooks/use-client-portal-redesign';
import {
  dismissPortfolioSplash,
  isPortfolioSplashDismissed,
} from 'src/features/client-portal/utils/portfolio-splash-dismissal';
import { graphql } from 'src/gql';
import { withClientGuard } from 'src/hocs/with-client-guard';
import { showPortfolioSplashScreen } from 'src/utils/client-roadmap';

import { CLIENT_STAGE_FULLY_ONBOARDED } from '@savvy/constants/client-stages';
import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

const RECONCILE_CLIENT_PROFILE_STAGE_MUTATION = graphql(/* GraphQL */ `
  mutation ReconcileClientProfileStageFromDashboard {
    reconcileClientProfileStage(input: {}) {
      clientStage
    }
  }
`);

export const dashboardQuery = graphql(/* GraphQL */ `
  query TasksFromDashboard {
    tasks(household: true) {
      ...ClientRoadmap
    }
  }
`);

const CLIENT_QUERY = graphql(/* GraphQL */ `
  query ClientQueryFromDashboard {
    client {
      providerAccounts {
        source
      }
    }
  }
`);

export function Dashboard() {
  const { user, reauth } = useAuth();
  const router = useRouter();
  const isSmallScreen = useIsSmallScreen();
  const isUserFullyOnboarded = user?.clientStage === CLIENT_STAGE_FULLY_ONBOARDED;
  const homeDecision = useClientPortalRedesign('home');
  const showRedesignedHome = homeDecision === 'redesign';
  const [splashDismissedNow, setSplashDismissedNow] = useState(false);
  const splashDismissed = splashDismissedNow || isPortfolioSplashDismissed(user?.id);
  const { loading: taskLoading, data: taskData } = useQueryWithErrorLogging(dashboardQuery, {
    skip: showRedesignedHome,
  });
  const { loading: clientLoading, data: clientData } = useQueryWithErrorLogging(CLIENT_QUERY, {
    skip: isUserFullyOnboarded || splashDismissed,
  });
  const [reconcileClientProfileStage] = useMutationWithErrorLogging(RECONCILE_CLIENT_PROFILE_STAGE_MUTATION);
  const hasReconciledStage = useRef(false);
  // Held so the opening splash covers this page's own first load too, rather than releasing onto the
  // skeleton below and reading as a second loading state. Ignored once the splash has begun to dissolve.
  useSplashHold('dashboard-first-load', taskLoading || clientLoading);

  useEffect(() => {
    if (homeDecision === 'legacy' && isUserFullyOnboarded) {
      void router.replace('/portfolio');
    }
  }, [homeDecision, isUserFullyOnboarded, router]);

  // Self-heal client_profile.stage if it's lagging behind task statuses. Fires
  // once per dashboard mount for non-fully-onboarded clients. The mutation is
  // idempotent (no-op when stage is already correct); we only refetch the
  // CurrentUser query when the stage actually moved.
  //
  // The ref is set synchronously to prevent a double-dispatch from concurrent
  // re-renders while the mutation is in flight, and reset on error so a
  // network/server failure doesn't permanently lock out the next attempt.
  useEffect(() => {
    if (hasReconciledStage.current) return;
    if (!user?.clientStage || isUserFullyOnboarded) return;
    hasReconciledStage.current = true;

    void (async () => {
      try {
        const result = await reconcileClientProfileStage();
        const newStage = result.data?.reconcileClientProfileStage?.clientStage;
        if (newStage && newStage !== user.clientStage) {
          await reauth();
        }
      } catch {
        hasReconciledStage.current = false;
      }
    })();
  }, [user?.clientStage, isUserFullyOnboarded, reconcileClientProfileStage, reauth]);

  if (homeDecision === 'pending') {
    return <ClientPortalVersionPending />;
  }

  if (taskLoading || clientLoading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col gap-5 px-4 pt-5 md:px-10 md:pt-8">
          <div className="flex items-center gap-4">
            <Skeleton className="size-16 rounded-full" />
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-56" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
            <div className="min-w-0 flex-1">
              <div className="rounded-xl border border-border-light bg-card p-6">
                <Skeleton className="h-9 w-48" />
                <Skeleton className="mt-2 h-5 w-44" />
                <Skeleton className="mt-6 h-64 w-full" />
              </div>
            </div>
            <div className="w-full lg:w-80 lg:shrink-0">
              <div className="rounded-xl border border-border-light bg-card p-6">
                <Skeleton className="h-6 w-24" />
                <div className="mt-4 flex flex-col gap-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (user?.clientStage && !splashDismissed && showPortfolioSplashScreen(user.clientStage, clientData?.client)) {
    return (
      <PortfolioSplashScreen
        onAdvanced={() => setSplashDismissedNow(true)}
        onDismiss={
          showRedesignedHome
            ? () => {
                dismissPortfolioSplash(user.id);
                setSplashDismissedNow(true);
              }
            : undefined
        }
      />
    );
  } else if (showRedesignedHome) {
    return (
      <DashboardLayout>
        <ClientPortalHome />
      </DashboardLayout>
    );
  } else if (isUserFullyOnboarded) {
    return null;
  }
  return isSmallScreen ? (
    <ClientRoadmap taskFrags={taskData?.tasks ?? []} />
  ) : (
    <DashboardLayout
      headerTitle={`Hello, ${user?.firstName}!`}
      hideBorder
      headerRightContent={
        <Text variant="subtitle-lg" color="secondary">
          {DateTime.now().toFormat('LLLL dd, yyyy')}
        </Text>
      }
    >
      <Head>
        <title>Progress</title>
      </Head>
      <ClientRoadmap taskFrags={taskData?.tasks ?? []} />
    </DashboardLayout>
  );
}

export default withClientGuard(Dashboard);
